import { useCallback, useEffect, useRef, useState } from "react";
import { ImageUp, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { AGENT_EVENT, useAgent } from "@/hooks/useAgent";
import { PERMISSIONS, ROLES, ROLE_ORDER, can } from "@/lib/roles";
import { cn, initials } from "@/lib/utils";
import { useFileDrop } from "@/hooks/useFileDrop";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { SettingsCard } from "@/components/ui/settings-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, GhostButton, Loading, PageHeader, PrimaryButton, SecondaryButton, StatusCircle, errorCls, helpCls, inputCls, labelCls } from "@/components/agent/ui";

const LOGO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];
const ASSIGNABLE = ["admin", "agent", "viewer"];

const Business = ({ org, onSaved, editable }) => {
  const [name, setName] = useState(org.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);

  const patch = async (body, message) => {
    try {
      onSaved((await api.patch("/org", body)).data);
      window.dispatchEvent(new Event(AGENT_EVENT));
      toast.success(message);
      return true;
    } catch (err) {
      toast.error(errorText(err));
      return false;
    }
  };
  const pick = (file) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type) || file.size > 500 * 1024) return toast.error("Use a JPG, PNG or SVG under 500KB.");
    const reader = new FileReader();
    reader.onload = () => patch({ logo: reader.result }, "Logo updated");
    reader.readAsDataURL(file);
  };
  const drop = useFileDrop({ accept: LOGO_TYPES.join(","), disabled: !editable, onFiles: ([f]) => pick(f), onReject: () => toast.error("Use a JPG, PNG or SVG under 500KB.") });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onSaved((await api.patch("/org", { name: name.trim() })).data);
      window.dispatchEvent(new Event(AGENT_EVENT));
      toast.success("Business name saved");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsCard title="Business" description="Applicants see your business name on your apply and criteria pages." id="business" testid="org-business">
      <div {...drop.bind} className={cn("-m-2 flex items-center gap-4 rounded-xl p-2 transition-colors", drop.dragging && "bg-brand/10 ring-2 ring-brand")} data-testid="org-logo-drop">
        {org.logo
          ? <img src={org.logo} alt="" className="size-16 shrink-0 rounded-xl border bg-background object-contain p-1" data-testid="org-logo" />
          : <span className="grid size-16 shrink-0 place-items-center rounded-xl bg-brand text-xl font-semibold text-brand-foreground" data-testid="org-logo">{initials(org.name)}</span>}
        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium">Logo</p>
          {editable && (
            <div className="flex gap-2">
              <SecondaryButton onClick={() => input.current?.click()} testid="org-logo-upload"><ImageUp /> {org.logo ? "Change" : "Upload"}</SecondaryButton>
              {org.logo && <GhostButton onClick={() => patch({ logo: "" }, "Logo removed")} testid="org-logo-remove"><Trash2 /> Remove</GhostButton>}
            </div>
          )}
          <p className={helpCls}>{drop.dragging ? "Drop to upload your logo." : `Square works best. JPG, PNG or SVG, up to 500KB.${editable ? " You can also drag it here." : ""}`}</p>
        </div>
        <input ref={input} type="file" accept={LOGO_TYPES.join(",")} className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="org-logo-input" />
      </div>
      <form onSubmit={save} className="flex items-end gap-3 border-t pt-4" data-testid="settings-form">
        <div className="flex-1">
          <Field label="Business name">
            <input required maxLength={120} value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="settings-business-name" />
          </Field>
        </div>
        {editable && <PrimaryButton type="submit" size="default" isLoading={busy} disabled={!name.trim() || name.trim() === org.name} testid="settings-save-button">Save</PrimaryButton>}
      </form>
      {error && <p className={errorCls} data-testid="settings-error">{error}</p>}
    </SettingsCard>
  );
};

const RoleSelect = ({ value, onChange, className, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger size="sm" className={className} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent align="end">
      {ASSIGNABLE.map((r) => <SelectItem key={r} value={r} data-testid={`${testid}-${r}`}>{ROLES[r].label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const InviteDialog = ({ open, onClose, onInvited }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("agent");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/org/members", { email: email.trim(), role });
      if (data.email_sent) toast.success(`Invite sent to ${data.email}`);
      else toast.warning("The invite email couldn't be sent. Try again later.");
      onInvited();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite a member</DialogTitle>
          <DialogDescription>They'll get an email with a link to join your organization.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Email">
            <input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@agency.co.uk" className={inputCls} data-testid="invite-email" />
          </Field>
          <div className="grid gap-2">
            <span className={labelCls}>Role</span>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger size="input" className="h-9 w-full" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSIGNABLE.map((r) => <SelectItem key={r} value={r} data-testid={`invite-role-${r}`}>{ROLES[r].label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className={helpCls}>{ROLES[role].description}</span>
          </div>
          {error && <p className={errorCls} data-testid="invite-error">{error}</p>}
          <DialogFooter>
            <SecondaryButton size="default" onClick={onClose} testid="invite-cancel">Cancel</SecondaryButton>
            <PrimaryButton type="submit" size="default" isLoading={busy} testid="invite-submit">Send invite</PrimaryButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RemoveDialog = ({ member, business, onClose, onRemoved }) => {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/org/members/${member.id}`);
      toast.success(`${member.name || member.email} removed`);
      onRemoved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="remove-dialog">
        <DialogHeader>
          <DialogTitle>Remove {member?.name || member?.email}?</DialogTitle>
          <DialogDescription>They'll lose access to {business} straight away. Their past decisions stay on record.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <SecondaryButton size="default" onClick={onClose} testid="remove-cancel">Cancel</SecondaryButton>
          <Button variant="destructive" isLoading={busy} onClick={remove} data-testid="remove-confirm">Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MemberRow = ({ m, i, me, manage, onRole, onRemove }) => (
  <div className="flex items-center gap-3" data-testid={`member-${i}`}>
    <span className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-medium">{initials(m.name || m.email)}</span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
        {m.name || m.email}
        {m.id === me && <span className="font-normal text-muted-foreground">(you)</span>}
        {m.status === "invited" && <Badge variant="outline" data-testid={`member-invited-${i}`}>Invited</Badge>}
      </div>
      <p className="break-all text-xs text-muted-foreground">{m.email}</p>
    </div>
    {m.role === "owner" || !manage || m.id === me
      ? <span className="w-28 px-2.5 text-sm text-muted-foreground" data-testid={`member-role-${i}`}>{ROLES[m.role].label}</span>
      : <RoleSelect value={m.role} onChange={(r) => onRole(m, r)} className="w-28" testid={`member-role-${i}`} />}
    {manage && m.role !== "owner" && m.id !== me
      ? <Button variant="ghost-muted" size="icon-sm" aria-label="Remove member" onClick={() => onRemove(m)} data-testid={`member-remove-${i}`}><Trash2 /></Button>
      : <span className="size-7 shrink-0" />}
  </div>
);

const Members = ({ business, me, manage }) => {
  const [members, setMembers] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(() => api.get("/org/members").then((r) => setMembers(r.data)).catch((err) => toast.error(errorText(err))), []);
  useEffect(() => {
    load();
  }, [load]);

  const changeRole = async (m, role) => {
    try {
      await api.patch(`/org/members/${m.id}`, { role });
      toast.success(`${m.name || m.email}'s role changed to ${ROLES[role].label}`);
      load();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <SettingsCard title="Members" description={`People at ${business} who can sign in to Landin.`} divide id="members" testid="org-members"
      action={manage && <SecondaryButton onClick={() => setInviting(true)} testid="invite-button"><Plus /> Invite member</SecondaryButton>}>
      {!members ? <Loading /> : members.map((m, i) => <MemberRow key={m.id} m={m} i={i} me={me} manage={manage} onRole={changeRole} onRemove={setRemoving} />)}
      <InviteDialog key={`invite-${inviting}`} open={inviting} onClose={() => setInviting(false)} onInvited={() => { setInviting(false); load(); }} />
      <RemoveDialog member={removing} business={business} onClose={() => setRemoving(null)} onRemoved={() => { setRemoving(null); load(); }} />
    </SettingsCard>
  );
};

const Access = () => (
  <SettingsCard title="Who can do what" description="What each role is allowed to do in Landin." flush id="roles" testid="org-access">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b">
          <th className="px-4 py-2.5 text-left font-normal text-muted-foreground">Permission</th>
          {ROLE_ORDER.map((r) => <th key={r} className="w-20 px-2 py-2.5 text-center font-medium">{ROLES[r].label}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y">
        {PERMISSIONS.map((p) => (
          <tr key={p.perm} data-testid={`access-${p.perm}`}>
            <td className="px-4 py-2.5">{p.label}</td>
            {ROLE_ORDER.map((r) => (
              <td key={r} className="px-2 py-2.5"><span className="flex justify-center">
                {can(r, p.perm) ? <StatusCircle status="passed" /> : <Minus className="size-4 text-muted-foreground/50" />}
              </span></td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </SettingsCard>
);

export default function Organization() {
  const agent = useAgent();
  const [org, setOrg] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/org").then((r) => setOrg(r.data)).catch((err) => setError(errorText(err)));
  }, []);

  return (
    <AgentLayout>
      <PageHeader title="Organization" description="Your business, and who on your team can use Landin." />
      {error && <p className={errorCls} data-testid="org-error">{error}</p>}
      {!org || !agent ? !error && <Loading testid="org-loading" /> : (
        <>
          <Business key={org.name + org.logo.length} org={org} onSaved={setOrg} editable={can(org.my_role, "business")} />
          <Members business={org.name} me={agent.id} manage={can(org.my_role, "members")} />
          <Access />
        </>
      )}
    </AgentLayout>
  );
}
